import { useEffect, useState } from 'react';
import type { AvvisoDiServizio } from '@draughts/shared';
import { api } from '../services/api';
import { log } from '../utils/logger';

/**
 * L'avviso di servizio della home, letto dal server.
 *
 * Due scelte di questo hook non sono di gusto, hanno un motivo che si vede a
 * schermo; sono spiegate dove stanno (la cache a modulo, e i due inneschi).
 */
interface VoceCache {
  avviso: AvvisoDiServizio | null;
  ts: number;
}

/**
 * Cache a livello di MODULO e non di istanza.
 *
 * `App.tsx` e' uno state-router a return anticipati: a ogni navigazione smonta
 * l'albero, quindi uno stato interno al componente farebbe sparire e ricomparire
 * la striscia ogni volta che si torna in home. Stesso schema, e stesso motivo,
 * di `useMostActivePlayers` e `dashboard-cache`.
 *
 * Esportata solo per i test.
 */
export let cacheAvviso: VoceCache | null = null;

/**
 * Un minuto. Non e' il TTL del server (dieci secondi): e' il ritmo con cui vale
 * la pena richiedere, e insieme al `max-age=10` della risposta fa sparire un
 * avviso spento entro circa ottanta secondi.
 */
export const TTL_AVVISO_MS = 60_000;

export function svuotaCacheAvviso(): void {
  cacheAvviso = null;
}

function scaduta(adesso: number): boolean {
  return cacheAvviso === null || adesso - cacheAvviso.ts >= TTL_AVVISO_MS;
}

export function useAvvisoDiServizio(): AvvisoDiServizio | null {
  // Il seme dalla cache ignora il TTL: tornando in home la striscia e' subito
  // li', e si aggiorna dopo (stale-while-revalidate).
  const [avviso, setAvviso] = useState<AvvisoDiServizio | null>(
    () => cacheAvviso?.avviso ?? null,
  );

  useEffect(() => {
    const controller = new AbortController();
    let vivo = true;

    const chiedi = async () => {
      if (!scaduta(Date.now())) return;
      try {
        const risposta = await api.getAvvisoDiServizio({ signal: controller.signal });
        if (!vivo) return;
        cacheAvviso = { avviso: risposta.avviso, ts: Date.now() };
        setAvviso(risposta.avviso);
      } catch (err) {
        if (controller.signal.aborted) return;
        /*
         * Un guasto NON cancella quello che c'e' gia' a schermo — al contrario
         * di `useMostActivePlayers`, che su errore torna lista vuota.
         *
         * La differenza ha un motivo preciso: i secondi di `pm2 delete` +
         * `start` di un deploy, in cui questa chiamata prende un 502, sono
         * esattamente i secondi in cui l'avviso di riavvio e' a schermo. Sarebbe
         * assurdo che a farlo sparire fosse il riavvio che annuncia.
         */
        log.error('Avviso', 'lettura dell\'avviso di servizio fallita', err);
      }
    };

    void chiedi();

    /*
     * Il secondo innesco: il ritorno in primo piano.
     *
     * Il solo montaggio non basta, e il caso che scopre e' quello che conta: chi
     * resta fermo in home, o lascia la scheda aperta col telefono in tasca, non
     * vedrebbe MAI comparire l'avviso — cioe' proprio la persona che un riavvio
     * imminente coglierebbe nel mezzo.
     *
     * E non e' un `setInterval`: a scheda nascosta qui non gira nulla. Su questa
     * schermata il lavoro a riposo e' stato misurato e tolto una volta (vedi
     * `moto-perpetuo-home.test.ts`), e non va rimesso dentro di straforo.
     */
    const alRitorno = () => {
      if (document.visibilityState === 'visible') void chiedi();
    };
    document.addEventListener('visibilitychange', alRitorno);

    return () => {
      vivo = false;
      controller.abort();
      document.removeEventListener('visibilitychange', alRitorno);
    };
  }, []);

  return avviso;
}
